// The receipt names the MASTHEAD, never the pipeline.
//
// Card data carries whatever the ingest feed was called (reg-smh-top, abc-just-in,
// 7news-au, google-trends-au). Those are pipeline IDs, and for a product whose whole
// pitch is "the publication, named, on every single card", rendering one is the fastest
// way to look fake. This module is the ONE place that turns a stored source name into a
// display name, shared by the SPA (src imports it via _mastheads.d.ts, the _device.js
// pattern) and every Pages Function. public/play.js is a standalone static script that
// cannot import modules; it carries a deliberately tiny inline copy of the same idea,
// with a comment pointing here. If you add a masthead, add it in both.
//
// Two layers, deterministic first:
//  1. A curated map for every feed id the pipeline actually uses (exact match, after
//     lowercasing and stripping the reg-/rss-/feed- ingest prefixes).
//  2. A cleanup fallback for ids the map does not know: strip prefixes and routing
//     suffixes, split on dashes, title-case, and keep broadcaster acronyms upper-case.
//     "courier-mail-au" -> "Courier Mail" beats "reg-courier-mail-au" every day.
// Real display names (anything with a space or uppercase already) pass through untouched.

const MASTHEADS = {
  "smh": "The Sydney Morning Herald",
  "smh-top": "The Sydney Morning Herald",
  "the-age": "The Age",
  "theage": "The Age",
  "abc": "ABC News",
  "abc-news": "ABC News",
  "abc-just-in": "ABC News",
  "abc-au": "ABC News",
  "7news": "7NEWS",
  "7news-au": "7NEWS",
  "9news": "9News",
  "9news-au": "9News",
  "guardian": "The Guardian Australia",
  "guardian-au": "The Guardian Australia",
  "news-com-au": "news.com.au",
  "afr": "The Australian Financial Review",
  "sbs": "SBS News",
  "sbs-au": "SBS News",
  "perthnow": "PerthNow",
  "perth-now": "PerthNow",
  "crikey": "Crikey",
  "afl": "AFL.com.au",
  "afl-com-au": "AFL.com.au",
  "nrl": "NRL.com",
  "nrl-com-au": "NRL.com",
  "google-trends": "Google Trends",
  "google-trends-au": "Google Trends",
  "canberra-times": "The Canberra Times",
  "brisbane-times": "Brisbane Times",
  "courier-mail": "The Courier Mail",
  "adelaide-now": "Adelaide Now",
  "wa-today": "WAtoday",
  "watoday": "WAtoday",
  "canberratimes": "The Canberra Times",
  // Australian Community Media's regional mastheads. The pipeline stores them by bureau
  // ("acm-ballarat"), and the fallback prettifier turned that into "Acm Ballarat", which is
  // not the name on any masthead and not a name any reader has heard.
  "acm-ballarat": "The Courier",
  "acm-bendigo": "Bendigo Advertiser",
  "acm-newcastle": "Newcastle Herald",
  "acm-examiner": "The Examiner",
  "acm-standard": "The Standard",
  "acm-advocate": "The Advocate",
  "acm-dailyadvertiser": "The Daily Advertiser",
  "acm-illawarra": "Illawarra Mercury",
  "acm-canberra": "The Canberra Times",
  // region.com.au. Stored as "region-au", and the routing-suffix rule turned it into the bare
  // word "Region", which on a receipt line reads as a category label rather than a publisher.
  // Named by its domain, the way news.com.au already is: unambiguous and not invented.
  "region-au": "region.com.au",
  "region": "region.com.au",
};

const INGEST_PREFIX = /^(reg|rss|feed)-/;
// Routing words a feed id appends that say nothing about the publication.
const ROUTING_SUFFIX = /-(top|latest|just-in|breaking|au)$/;
const ACRONYMS = new Set(["abc", "sbs", "afl", "nrl", "afr", "smh", "uk", "us", "nsw", "qld", "wa", "sa", "act", "nt"]);

/** True when a stored source name is a pipeline id rather than a human name: all
 *  lowercase alphanumerics and dashes, no spaces, no dots, no capitals. */
function looksLikeSlug(value) {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value);
}

/**
 * Display name for a card's stored source. Real names pass through unchanged; pipeline
 * ids come back as mastheads. Returns "" for empty input so callers can `|| fallback`.
 */
export function prettySourceName(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  // Anything with a space or an uppercase letter is already a display name.
  if (!looksLikeSlug(value)) return value;
  let slug = value.toLowerCase().replace(INGEST_PREFIX, "");
  if (MASTHEADS[slug]) return MASTHEADS[slug];
  const trimmed = slug.replace(ROUTING_SUFFIX, "");
  if (MASTHEADS[trimmed]) return MASTHEADS[trimmed];
  // Unknown id: make it presentable rather than shipping the plumbing.
  return trimmed
    .split("-")
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

/**
 * True when we can NAME this source, rather than merely tidy it up.
 * prettySourceName always returns something presentable, which is right for a card in the
 * feed and not enough for a surface whose entire pitch is the name: "Acm Ballarat" is
 * presentable and is not a masthead. Use this wherever printing a guess would cost more
 * than printing nothing (the landing page picks its lead stories with it).
 */
export function isKnownMasthead(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return false;
  // Already a display name (a space, a dot, or a capital): whoever stored it typed a name.
  if (!looksLikeSlug(value)) return true;
  const slug = value.toLowerCase().replace(INGEST_PREFIX, "");
  return !!(MASTHEADS[slug] || MASTHEADS[slug.replace(ROUTING_SUFFIX, "")]);
}

export { MASTHEADS };
